import React, {useEffect, useRef, useState} from 'react';
import {Button, Input, Form, Space, Select, Modal, Tag} from 'antd';
import {useLazyQuery, useQuery} from '@apollo/client';
import {
    GET_ALL_STATES,
    GET_CITIES_BY_STATE,
    GetAllStatesResponse,
    GetCitiesByStateResponse
} from '../../../../graphql/queries/getStatesAndCities';
import { 
    GET_ZIPCODE_DETAIL,
    GetZipcodeDetailResponse,
    ZipcodeType 
} from '../../../pickup/appointment/graphql/queries/getLocationData';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { t } from '../../../../i18n';
import AddressGuideModal from './AddressGuideModal';

const countryOptions = [{ label: 'US', value: 'US' }];

export interface AddressInfo {
    fullName: string;
    phoneCountry: string;
    phoneNumber: string;
    phoneExt: string;
    addressLine1: string;
    addressLine2: string;
    country: string;
    state: string;
    city: string;
    zipcode: string;
    geo: string;
    isFinished?: boolean;
}

export const splicingAddress = (data?: AddressInfo) => {
    if (data){
        return [data?.addressLine1,data?.addressLine2,data?.city,data?.state,data?.zipcode,data?.country].every(v => !v)?
            '-' : [data?.addressLine1,data?.addressLine2,data?.city,data?.state,data?.zipcode,data?.country].filter(Boolean).join(',');
    }
    return '-';
};

export const splicingPhoneInfo = (data?: AddressInfo) => {
    // 处理数据为空的情况
    if (!data) return '-';

    // 提取并清理各个字段，确保不会出现undefined
    const phoneCountry = data.phoneCountry?.trim() || '';
    const phoneNumber = data.phoneNumber?.trim() || '';
    const phoneExt = data.phoneExt?.trim() || '';

    // 构建基础电话号码部分
    const basePhone = [phoneCountry, phoneNumber].filter(Boolean).join(' ');

    // 如果没有基础电话号码，返回-
    if (!basePhone) return '-';

    // 有分机号时添加ext前缀
    if (phoneExt) {
        return `${basePhone} ${t('delivery.parcels.label.ext')}:${phoneExt}`;
    }

    return basePhone;
};

export const splicingGeoInfo = (latitude?: number | null, longitude?: number | null) => {
    return latitude && longitude 
        ? `${latitude},${longitude}`
        : '-';
};

export const parseGeoString = (geo?: string | null): { latitude: number; longitude: number } | null => {
    if (!geo || geo === '-') {
        return null;
    }
    const parts = geo.split(',').map((part) => part.trim());
    if (parts.length !== 2) {
        return null;
    }
    const latitude = Number(parts[0]);
    const longitude = Number(parts[1]);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
        return null;
    }
    return { latitude, longitude };
};

export const formatGeoDisplay = (
    geo?: string | null,
    pending?: { latitude: number; longitude: number } | null
): string => {
    const base = geo && geo !== '-' ? geo : '-';
    if (!pending) {
        return base;
    }
    const pendingText = `${pending.latitude},${pending.longitude}`;
    const pendingLabel = t('delivery.parcels.label.addressLocationPending');
    if (base === '-') {
        return `${pendingText} (${pendingLabel})`;
    }
    return `${base}（${pendingText} ${pendingLabel}）`;
};

export const buildLocationPendingFromDetail = (
    detail?: {
        addressLocationChangePending?: boolean;
        pendingLatitude?: number | null;
        pendingLongitude?: number | null;
    } | null
): { latitude: number; longitude: number } | null => {
    if (!detail?.addressLocationChangePending) {
        return null;
    }
    if (detail.pendingLatitude == null || detail.pendingLongitude == null) {
        return null;
    }
    return {
        latitude: detail.pendingLatitude,
        longitude: detail.pendingLongitude,
    };
};

interface Props {
    value?: AddressInfo;
    onChange: (val: AddressInfo) => void;
    editingBlock?: null | 'recipient' | 'dimension';
    onEdit?: () => void;
    onEditCancel?: () => void;
    originalRecipientInfo? : AddressInfo
    onSave?: (formData: AddressInfo) => void;
    /** 仅保存收件人姓名/电话；传入后展示独立联系人编辑入口 */
    onSaveContact?: (formData: AddressInfo) => void;
    /** 是否允许修改地址定位（经纬度）；未传时与地址编辑窗口一致 */
    canEditAddressLocation?: boolean;
    /** 提交新的经纬度（审核后生效） */
    onSaveLocation?: (latitude: number, longitude: number) => void | Promise<void>;
    /** 待审核的新经纬度，用于展示「原坐标（新坐标审核中）」 */
    locationPending?: { latitude: number; longitude: number } | null;
}

const AddressInfoBlock: React.FC<Props> = ({
    value,
    onChange,
    editingBlock,
    onEdit,
    onEditCancel,
    originalRecipientInfo,
    onSave,
    onSaveContact,
    canEditAddressLocation,
    onSaveLocation,
    locationPending,
}) => {
    const [editing, setEditing] = useState(false);
    const [editingContact, setEditingContact] = useState(false);
    const [editingLocation, setEditingLocation] = useState(false);
    const [locationSubmitting, setLocationSubmitting] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [address,setAddress] = useState<string>('');
    const [canEdit,setCanEdit] = useState(true);
    const [recipientInfo,setRecipientInfo] = useState<string>('');
    const [form] = Form.useForm();
    const [locationForm] = Form.useForm<{ latitude: string; longitude: string }>();
    const hasOriginalRecipient = Boolean(originalRecipientInfo);
    const [stateOptions, setStateOptions] = useState<{ label: string; value: string }[]>([]);
    const [cityOptions, setCityOptions] = useState<{ label: string; value: string }[]>([]);

    const { data: statesData, loading: statesLoading } = useQuery<GetAllStatesResponse>(GET_ALL_STATES, {
        fetchPolicy: 'cache-and-network'
    });

    const [getCitiesByState, { data: citiesData, loading: citiesLoading }] = useLazyQuery<GetCitiesByStateResponse>(GET_CITIES_BY_STATE, {
        fetchPolicy: 'cache-and-network'
    });

    const [getZipcodeDetail, { data: zipcodeData, error: zipcodeError }] = useLazyQuery<GetZipcodeDetailResponse>(GET_ZIPCODE_DETAIL, {
        fetchPolicy: 'cache-and-network'
    });

    // 使用useEffect监听查询结果变化
    useEffect(() => {
        if (zipcodeData) {
            if (zipcodeData.getZipcodeByCode) {
                handleZipcodeDataUpdate(zipcodeData.getZipcodeByCode);
                setZipcodeStatus('success');
            } else {
                setZipcodeStatus('error');
            }
        }
    }, [zipcodeData]);

    useEffect(() => {
        if (zipcodeError) {
            setZipcodeStatus('error');
        }
    }, [zipcodeError]);

    // 处理zipcode数据更新的函数
    const handleZipcodeDataUpdate = (zipcodeDetail: ZipcodeType) => {
        // 直接更新从zipcode查询得到的字段
        form.setFieldsValue({
            country: 'US', // zipcode查询默认为美国
            state: zipcodeDetail.state,
            city: zipcodeDetail.city
        });
        
        // 更新城市选项
        if (zipcodeDetail.state) {
            getCitiesByState({ variables: { state: zipcodeDetail.state } });
        }
    };

    // 防抖处理zipcode输入
    const [zipcodeTimer, setZipcodeTimer] = useState<NodeJS.Timeout | null>(null);
    const [zipcodeStatus, setZipcodeStatus] = useState<'success' | 'error' | null>(null);

    /**
     * 邮编输入规范化：仅允许5位数字，去除空格及非数字字符，与 DB 约束一致，避免「事件已记、地址未改」等不一致。
     */
    const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value ?? '';
        const normalized = raw.trim().replace(/\D/g, '').slice(0, 5);
        form.setFieldValue('zipcode', normalized);

        if (zipcodeTimer) {
            clearTimeout(zipcodeTimer);
            setZipcodeTimer(null);
        }

        // 邮编一旦变更即重置校验状态，避免全选粘贴新邮编时残留上一个邮编的 success
        setZipcodeStatus(null);
        if (normalized.length < 5) {
            return;
        }

        // 设置新的防抖定时器，直接查询数据库
        const timer = setTimeout(() => {
            getZipcodeDetail({ variables: { zipcode: normalized } });
        }, 500); // 500ms防抖
        setZipcodeTimer(timer);
    };

    useEffect(() => {
        setAddress(splicingAddress(value));
        setRecipientInfo(splicingAddress(originalRecipientInfo));
        setCanEdit(value?.isFinished === false);
        if (value?.state) {
            getCitiesByState({ variables: { state: value.state } });
        }
        
        // 清理定时器
        return () => {
            if (zipcodeTimer) {
                clearTimeout(zipcodeTimer);
            }
        };
    }, [value, originalRecipientInfo, getCitiesByState]);

    const canEditLocation = canEditAddressLocation ?? canEdit;

    const handleEditLocation = () => {
        locationForm.setFieldsValue({ latitude: '', longitude: '' });
        setEditingLocation(true);
        setEditing(false);
        setEditingContact(false);
        if (onEdit) onEdit();
    };

    const handleCancelLocation = () => {
        setEditingLocation(false);
        locationForm.resetFields();
        if (onEditCancel) onEditCancel();
    };

    const handleSaveLocation = async (latitude: number, longitude: number) => {
        const current = parseGeoString(value?.geo);
        if (current && current.latitude === latitude && current.longitude === longitude) {
            Modal.info({
                title: t('delivery.parcels.error.sameGeoLocation'),
                content: t('delivery.parcels.error.sameGeoLocationMessage'),
            });
            return;
        }
        if (locationPending
            && locationPending.latitude === latitude
            && locationPending.longitude === longitude) {
            Modal.info({
                title: t('delivery.parcels.error.sameGeoLocation'),
                content: t('delivery.parcels.error.sameGeoLocationMessage'),
            });
            return;
        }
        if (!onSaveLocation) {
            Modal.info({
                title: t('delivery.parcels.label.editAddressLocation'),
                content: t('delivery.parcels.message.addressLocationSubmitPending'),
            });
            setEditingLocation(false);
            locationForm.resetFields();
            if (onEditCancel) onEditCancel();
            return;
        }
        setLocationSubmitting(true);
        try {
            await onSaveLocation(latitude, longitude);
            setEditingLocation(false);
            locationForm.resetFields();
            if (onEditCancel) onEditCancel();
        } finally {
            setLocationSubmitting(false);
        }
    };

    const handleSaveLocationInline = async () => {
        try {
            const values = await locationForm.validateFields();
            const latitude = Number(values.latitude);
            const longitude = Number(values.longitude);
            await handleSaveLocation(latitude, longitude);
        } catch (error) {
            console.error('Location form validation failed:', error);
        }
    };

    const handleEdit = () => {
        form.setFieldsValue(value);
        setEditing(true);
        setEditingContact(false);
        setEditingLocation(false);
        if (onEdit) onEdit();
    };
    const handleEditContact = () => {
        form.setFieldsValue({
            fullName: value?.fullName,
            phoneCountry: value?.phoneCountry,
            phoneNumber: value?.phoneNumber,
            phoneExt: value?.phoneExt,
        });
        setEditingContact(true);
        setEditing(false);
        setEditingLocation(false);
        if (onEdit) onEdit();
    };
    const handleCancel = () => {
        setEditing(false);
        setEditingContact(false);
        setEditingLocation(false);
        locationForm.resetFields();
        if (onEditCancel) onEditCancel();
    };
    useEffect(() => {
        if (statesData?.getAllStates) {
            setStateOptions(statesData.getAllStates.map(s => ({ label: s, value: s })));
        }
    }, [statesData]);

    useEffect(() => {
        if (citiesData?.getCitiesByState) {
            setCityOptions(citiesData.getCitiesByState.map(c => ({ label: c, value: c })));
        }
    }, [citiesData]);
    const normalizeAddressField = (field?: string) => (field ?? '').trim();

    const isAddressUnchanged = (newVal: Record<string, string | undefined>) => {
        const addressFields = ['addressLine1', 'addressLine2', 'country', 'state', 'city'] as const;
        const hasFieldChange = addressFields.some(
            (field) => normalizeAddressField(newVal[field]) !== normalizeAddressField(value?.[field])
        );
        if (hasFieldChange) {
            return false;
        }
        const zipcode = (newVal.zipcode ?? '').trim().replace(/\D/g, '').slice(0, 5);
        const originalZipcode = (value?.zipcode ?? '').trim().replace(/\D/g, '').slice(0, 5);
        return zipcode === originalZipcode;
    };

    // 保存中互斥：保存时需 await 邮编查询，期间重复点击不应触发第二次 onSave
    const savingRef = useRef(false);

    const handleSave = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        try {
            const newVal = await form.validateFields();

            if (isAddressUnchanged(newVal)) {
                handleCancel();
                return;
            }

            // 邮编：仅允许5位数字，与 DB 一致；变更时须查询成功
            const zipcode = (newVal.zipcode ?? '').trim().replace(/\D/g, '').slice(0, 5);
            const originalZipcode = (value?.zipcode ?? '').trim().replace(/\D/g, '').slice(0, 5);

            let zipcodeDetail: ZipcodeType | null = null;
            if (zipcode !== originalZipcode) {
                if (zipcode.length !== 5 || !/^\d{5}$/.test(zipcode)) {
                    Modal.error({
                        title: t('delivery.parcels.error.zipcodeNotFound'),
                        content: t('delivery.parcels.error.zipcodeNotFoundMessage'),
                    });
                    return;
                }
                // 不依赖防抖查询的状态快照（输入后 500ms 内点保存时仍为 null），
                // 清掉在途定时器后直接查询一次，按返回结果判定
                if (zipcodeTimer) {
                    clearTimeout(zipcodeTimer);
                    setZipcodeTimer(null);
                }
                try {
                    const result = await getZipcodeDetail({ variables: { zipcode } });
                    zipcodeDetail = result.data?.getZipcodeByCode ?? null;
                } catch (error) {
                    console.error('Zipcode lookup failed:', error);
                }
                if (!zipcodeDetail) {
                    Modal.error({
                        title: t('delivery.parcels.error.zipcodeNotFound'),
                        content: t('delivery.parcels.error.zipcodeNotFoundMessage'),
                    });
                    return;
                }
            }
            // 地址编辑时不展示/不修改收件人姓名与电话，
            // 但后端的地址变更输入类型可能仍要求这些字段，
            // 因此这里沿用当前 value 里的 fullName/phone*，只覆盖地址相关字段。
            // 若保存前自动回填尚未发生（状态不是 success），用查询结果回填 state/city，
            // 与慢速路径下 handleZipcodeDataUpdate 的效果一致；已回填过则保留表单中的值
            const autoFill = (zipcodeDetail && zipcodeStatus !== 'success')
                ? { country: 'US', state: zipcodeDetail.state, city: zipcodeDetail.city }
                : {};
            const updatedVal = {
                ...value,
                ...newVal,
                ...autoFill,
                zipcode,
                isFinished: value?.isFinished
            } as AddressInfo;
            onChange(updatedVal);
            setEditing(false);
            if (onSave) onSave(updatedVal);
            if (onEditCancel) onEditCancel();
        } catch (error) {
            console.error('Form validation failed:', error);
        } finally {
            savingRef.current = false;
        }
    };

    const handleSaveContact = async () => {
        try {
            const newVal = await form.validateFields(['fullName', 'phoneNumber', 'phoneCountry', 'phoneExt']);
            const phoneExt = newVal.phoneExt?.trim();
            if (phoneExt && phoneExt.length > 10) {
                Modal.error({
                    title: t('delivery.parcels.label.extensionTooLong'),
                    content: t('delivery.parcels.label.extensionTooLong'),
                });
                return;
            }
            const updatedVal = {
                ...value,
                fullName: newVal.fullName,
                phoneCountry: newVal.phoneCountry,
                phoneNumber: newVal.phoneNumber,
                phoneExt: phoneExt || '',
            } as AddressInfo;
            onChange(updatedVal);
            setEditingContact(false);
            if (onSaveContact) onSaveContact(updatedVal);
            if (onEditCancel) onEditCancel();
        } catch (error) {
            console.error('Contact form validation failed:', error);
        }
    };

    // 编辑态下以只读形式保留另一半信息，避免编辑联系人时看不到地址、编辑地址时看不到联系人
    const readOnlyRow = (label: string, content?: string) => (
        <div className="mb-3">
            <div className="text-gray-500 text-sm mb-1">{label}:</div>
            <div className="break-words">{content || '-'}</div>
        </div>
    );

    const contactText = (() => {
        const name = value?.fullName || '';
        const phone = splicingPhoneInfo(value);
        if (name && phone && phone !== '-') {
            return `${name} (${phone})`;
        }
        return name || phone || '-';
    })();

    const addressLabel = hasOriginalRecipient
        ? t('delivery.parcels.label.recipientAddrNew')
        : t('delivery.parcels.label.recipientAddr');
    const geoLabel = hasOriginalRecipient
        ? t('delivery.parcels.label.geoCodingNew')
        : t('delivery.parcels.label.geoCoding');
    const contactLabel = hasOriginalRecipient
        ? t('delivery.parcels.label.recipientNew')
        : t('delivery.parcels.label.recipient');

    const renderGeoRow = (
        label: string,
        geo?: string | null,
        showLocationEditButton = false,
        pending?: { latitude: number; longitude: number } | null
    ) => (
        <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
                <div className="text-gray-500 text-sm">{label}:</div>
                {showLocationEditButton
                    && !editingBlock
                    && !editing
                    && !editingContact
                    && !editingLocation
                    && canEditLocation
                    && !!onSaveLocation
                    && !pending && (
                    <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={handleEditLocation}
                        type="text"
                        title={t('delivery.parcels.label.editAddressLocation')}
                    />
                )}
            </div>
            <div className="break-words flex flex-wrap items-center gap-2">
                <span>
                    {(() => {
                        const base = geo && geo !== '-' ? geo : '-';
                        if (!pending) {
                            return base;
                        }
                        const pendingText = `${pending.latitude},${pending.longitude}`;
                        if (base === '-') {
                            return pendingText;
                        }
                        return `${base}（${pendingText}）`;
                    })()}
                </span>
                {pending && (
                    <Tag color="warning" bordered={false} className="text-sm font-semibold px-2.5 py-0.5 m-0">
                        {t('delivery.parcels.label.addressLocationPending')}
                    </Tag>
                )}
            </div>
        </div>
    );

    const renderLocationEditForm = () => (
        <div className="relative mb-3">
            <Form form={locationForm} layout="horizontal" className="small-error-message">
                <div className="text-gray-500 text-sm mb-1">{geoLabel}:</div>
                <div className="break-words mb-2">{value?.geo && value.geo !== '-' ? value.geo : '-'}</div>
                <div className="text-gray-500 text-sm mb-2">
                    {t('delivery.parcels.label.addressLocationEditHint')}
                </div>
                <Form.Item label="">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Form.Item
                            name="latitude"
                            noStyle
                            rules={[
                                { required: true, message: t('delivery.parcels.error.latitudeRequired') },
                                {
                                    validator: (_, fieldValue) => {
                                        const num = Number(fieldValue);
                                        if (Number.isNaN(num) || num < -90 || num > 90) {
                                            return Promise.reject(new Error(t('delivery.parcels.error.invalidLatitude')));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder={t('delivery.parcels.label.latitude')}
                                inputMode="decimal"
                                style={{ width: '140px' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="longitude"
                            noStyle
                            rules={[
                                { required: true, message: t('delivery.parcels.error.longitudeRequired') },
                                {
                                    validator: (_, fieldValue) => {
                                        const num = Number(fieldValue);
                                        if (Number.isNaN(num) || num < -180 || num > 180) {
                                            return Promise.reject(new Error(t('delivery.parcels.error.invalidLongitude')));
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder={t('delivery.parcels.label.longitude')}
                                inputMode="decimal"
                                style={{ width: '140px' }}
                            />
                        </Form.Item>
                    </div>
                </Form.Item>
                <Form.Item label="" style={{ marginBottom: '10px' }}>
                    <div className="absolute bottom-0 right-0 z-10">
                        <Space>
                            <Button
                                icon={<CheckOutlined />}
                                size="small"
                                loading={locationSubmitting}
                                onClick={handleSaveLocationInline}
                            />
                            <Button
                                icon={<CloseOutlined />}
                                size="small"
                                onClick={handleCancelLocation}
                                disabled={locationSubmitting}
                            />
                        </Space>
                    </div>
                </Form.Item>
            </Form>
        </div>
    );

    return (
        <div className="mb-4 relative address-info-block">
            <style>{`
                .address-info-block .ant-form-item-explain-error {
                    font-size: 12px !important;
                }
                .address-info-block .ant-form-item-explain.ant-form-item-explain-error {
                    font-size: 12px !important;
                }
                .address-info-block .small-error-message .ant-form-item-explain-error {
                    font-size: 12px !important;
                }
            `}</style>
            {editingLocation ? (
                <>
                    {readOnlyRow(contactLabel, contactText)}
                    {readOnlyRow(addressLabel, address)}
                    {renderLocationEditForm()}
                </>
            ) : editingContact ? (
                <>
                <div className="relative">
                <Form form={form} layout="horizontal">
                    <Form.Item
                        label={t('delivery.parcels.label.recipient')}
                        name="fullName"
                        rules={[
                            { required: true, message: t('delivery.parcels.error.recipientRequired') }
                        ]}
                        className="small-error-message"
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item label="">
                        <div className="flex items-center gap-2 flex-nowrap whitespace-nowrap">
                            <Form.Item name="phoneCountry" noStyle>
                                <Input
                                    style={{ width: '80px' }}
                                    placeholder="+1"
                                />
                            </Form.Item>
                            <Form.Item name="phoneNumber" noStyle
                                rules={[{ required: true, message: t('delivery.parcels.error.phoneNumberRequired') }]}
                            >
                                <Input placeholder="234-567-8912" style={{ width: '200px' }} />
                            </Form.Item>
                            <span className="mx-2 whitespace-nowrap">{t('delivery.parcels.label.ext')}:</span>
                            <Form.Item name="phoneExt" noStyle
                                rules={[{ pattern: /^\d*$/, message: 'Only numbers are allowed' }]}
                            >
                                <Input placeholder="010" style={{ width: '80px' }} />
                            </Form.Item>
                        </div>
                    </Form.Item>
                    <Form.Item label="" style={{ marginBottom: '10px' }}>
                        <div className="absolute bottom-0 right-0 z-10">
                            <Space>
                                <Button icon={<CheckOutlined />} size="small" onClick={handleSaveContact} />
                                <Button icon={<CloseOutlined />} size="small" onClick={handleCancel} />
                            </Space>
                        </div>
                    </Form.Item>
                </Form>
                </div>
                {readOnlyRow(addressLabel, address)}
                {renderGeoRow(geoLabel, value?.geo, true, locationPending)}
                </>
            ) : editing ? (
                <>
                {readOnlyRow(contactLabel, contactText)}
                <div className="relative">
                <Form form={form} layout="horizontal">
                    <div className="mb-2">
                        <Button type="link" className="px-0" style={{ height: 'auto', lineHeight: 1.2 }} onClick={() => setGuideOpen(true)}>
                            {t('delivery.parcels.guide.viewAddressGuide')}
                        </Button>
                    </div>
                    <Form.Item
                        label={t('delivery.parcels.label.recipientAddr')}
                        name="addressLine1"
                        rules={[
                            { required: true, message: t('delivery.parcels.error.addressRequired') }
                        ]}
                        className="small-error-message"
                    >
                        <Input placeholder="123 Main Street" />
                    </Form.Item>
                    <Form.Item name="addressLine2">
                        <Input placeholder="Apartment 5B" />
                    </Form.Item>
                    <Form.Item label="">
                        <div className="flex items-center">
                            <Form.Item name="country" noStyle>
                                <Select style={{ width: 'calc(23%)' }} options={countryOptions} placeholder={t('delivery.parcels.label.country')} />
                            </Form.Item>
                            <Form.Item name="state" noStyle>
                                <Select
                                    style={{ width: 'calc(23%)' }}
                                    options={stateOptions}
                                    placeholder={t('delivery.parcels.label.state')}
                                    loading={statesLoading}
                                    onChange={(state) => {
                                        form.setFieldValue('city', undefined);
                                        setCityOptions([]);
                                        if (state) {
                                            getCitiesByState({ variables: { state } });
                                        }
                                    }}
                                />
                            </Form.Item>
                            <Form.Item name="city" noStyle>
                                <Select
                                    style={{ width: 'calc(34%)' }}
                                    options={cityOptions}
                                    placeholder={t('delivery.parcels.label.city')}
                                    loading={citiesLoading}
                                    disabled={!form.getFieldValue('state')}
                                />
                            </Form.Item>
                            <Form.Item name="zipcode" noStyle>
                                <Input 
                                    style={{ width: 'calc(20%)' }} 
                                    placeholder={t('delivery.parcels.label.zipCode')}
                                    onChange={handleZipcodeChange}
                                    maxLength={5}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>
                    <Form.Item label="" style={{ marginBottom: '10px' }}>
                        <div className="absolute bottom-0 right-0 z-10">
                            <Space>
                                <Button icon={<CheckOutlined />} size="small" onClick={handleSave} />
                                <Button icon={<CloseOutlined />} size="small" onClick={handleCancel} />
                            </Space>
                        </div>
                    </Form.Item>
                    <AddressGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
                </Form>
                </div>
                {renderGeoRow(geoLabel, value?.geo, false, locationPending)}
                </>
            ) : (
                <>
                    {hasOriginalRecipient ? (
                        <>
                            {/* 收件人(新) - 上下布局 */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-gray-500 text-sm">{t('delivery.parcels.label.recipientNew')}:</div>
                                    {onSaveContact && !editingBlock && canEdit && (
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={handleEditContact}
                                            type="text"
                                            title={t('delivery.parcels.label.editRecipientContact')}
                                        />
                                    )}
                                </div>
                                <div className="break-words">
                                    {(() => {
                                        const name = value?.fullName || '';
                                        const phone = splicingPhoneInfo(value);
                                        if (name && phone && phone !== '-') {
                                            return `${name} (${phone})`;
                                        }
                                        return name || phone || '-';
                                    })()}
                                </div>
                            </div>
                            {/* 收件人地址(新) - 上下布局 */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-gray-500 text-sm">{t('delivery.parcels.label.recipientAddrNew')}:</div>
                                    {!editingBlock && !editingLocation && canEdit && (
                                        <Button icon={<EditOutlined />} size="small" onClick={handleEdit} type="text" />
                                    )}
                                </div>
                                <div className="break-words">{address || '-'}</div>
                            </div>
                            {renderGeoRow(
                                t('delivery.parcels.label.geoCodingNew'),
                                value?.geo,
                                true,
                                locationPending
                            )}

                            {/* 收件人 - 上下布局 */}
                            <div className="mb-3">
                                <div className="text-gray-500 text-sm mb-1">{t('delivery.parcels.label.recipient')}:</div>
                                <div className="break-words">
                                    {(() => {
                                        const name = originalRecipientInfo?.fullName || '';
                                        const phone = splicingPhoneInfo(originalRecipientInfo);
                                        if (name && phone && phone !== '-') {
                                            return `${name} (${phone})`;
                                        }
                                        return name || phone || '-';
                                    })()}
                                </div>
                            </div>
                            {/* 收件人地址 - 上下布局 */}
                            <div className="mb-3">
                                <div className="text-gray-500 text-sm mb-1">{t('delivery.parcels.label.recipientAddr')}:</div>
                                <div className="break-words">{recipientInfo || '-'}</div>
                            </div>
                            {renderGeoRow(t('delivery.parcels.label.geoCoding'), originalRecipientInfo?.geo)}
                        </>
                    ):(
                        <>
                            {/* 收件人 - 上下布局 */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-gray-500 text-sm">{t('delivery.parcels.label.recipient')}:</div>
                                    {onSaveContact && !editingBlock && canEdit && (
                                        <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={handleEditContact}
                                            type="text"
                                            title={t('delivery.parcels.label.editRecipientContact')}
                                        />
                                    )}
                                </div>
                                <div className="break-words">
                                    {(() => {
                                        const name = value?.fullName || '';
                                        const phone = splicingPhoneInfo(value);
                                        if (name && phone && phone !== '-') {
                                            return `${name} (${phone})`;
                                        }
                                        return name || phone || '-';
                                    })()}
                                </div>
                            </div>
                            {/* 收件人地址 - 上下布局 */}
                            <div className="mb-3">
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-gray-500 text-sm">{t('delivery.parcels.label.recipientAddr')}:</div>
                                    {!editingBlock && !editingLocation && canEdit && (
                                        <Button icon={<EditOutlined />} size="small" onClick={handleEdit} type="text" />
                                    )}
                                </div>
                                <div className="break-words">{address || '-'}</div>
                            </div>
                            {renderGeoRow(geoLabel, value?.geo, true, locationPending)}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default AddressInfoBlock;